// contexts/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const navigate = useNavigate();

  // ------------------------------------------------------------------
  // 1. RESTORE FROM LOCAL STORAGE ON MOUNT
  // ------------------------------------------------------------------
  useEffect(() => {
    if (API_URL) {
      axios.defaults.baseURL = API_URL;
    }

    // ---------- Request interceptor ----------
    axios.interceptors.request.use(
      (config) => {
        const authToken = localStorage.getItem('token');
        if (authToken) {
          config.headers.Authorization = `Bearer ${authToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // ---------- Response interceptor ----------
    // ✅ Only wipe auth data if the failing request is a session check
    //    (GET /unified-me). A failed profile save (PUT /unified-me)
    //    or any other protected route will NOT wipe the token.
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const method = error.config?.method?.toUpperCase();
        const url = error.config?.url || '';

        const isSessionCheck =
          method === 'GET' && url.includes('/unified-me');

        if (status === 401 && isSessionCheck) {
          console.log('401 on session check – cleaning auth data');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('userType');
          localStorage.removeItem('company');

          setToken(null);
          setCurrentUser(null);
          setUserType(null);
          setCompany(null);
        }

        return Promise.reject(error);
      }
    );

    // ----- Restore from localStorage -----
    const storedUser = localStorage.getItem('user');
    const storedUserType = localStorage.getItem('userType');
    const storedCompany = localStorage.getItem('company');

    if (storedUser && storedUserType) {
      try {
        const user = JSON.parse(storedUser);
        setCurrentUser(user);
        setUserType(storedUserType);
        if (storedCompany) setCompany(JSON.parse(storedCompany));
        setLoading(false);
        return;
      } catch (e) {
        clearAuth();
      }
    }

    loadUser();
  }, []);

  // ------------------------------------------------------------------
  // 2. loadUser
  // ------------------------------------------------------------------
  const loadUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/unified-me`);
      if (response.data.success) {
        const { user, company: companyData } = response.data.data;

        let actualUserType = user?.userType;
        if (!actualUserType && user) {
          if (
            user.userCode?.startsWith('USERCOMP') ||
            user.role === 'admin' ||
            user.role === 'sales' ||
            user.role === 'cashier' ||
            user.role === 'purchase' ||
            user.role === 'user'
          ) {
            actualUserType = 'company';
          } else {
            actualUserType = 'system';
          }
        }

        setCurrentUser(user);
        setUserType(actualUserType);
        setCompany(companyData);

        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userType', actualUserType);
        if (companyData) {
          localStorage.setItem('company', JSON.stringify(companyData));
        } else if (actualUserType === 'company' && user.company) {
          localStorage.setItem('company', JSON.stringify({ _id: user.company }));
        }
      } else {
        clearAuth();
      }
    } catch (error) {
      const storedUser = localStorage.getItem('user');
      const storedUserType = localStorage.getItem('userType');
      const storedCompany = localStorage.getItem('company');

      if (storedUser && storedUserType) {
        try {
          const user = JSON.parse(storedUser);
          setCurrentUser(user);
          setUserType(storedUserType);
          if (storedCompany) setCompany(JSON.parse(storedCompany));
        } catch (e) {
          clearAuth();
        }
      } else {
        clearAuth();
      }
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // 3. refreshCompanyToken
  // ------------------------------------------------------------------
  const refreshCompanyToken = async () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) return;

      const user = JSON.parse(storedUser);
      const response = await axios.post(`${API_URL}/api/auth/refresh-token`, {
        userId: user.id,
        userType: 'company',
      });

      if (response.data.success && response.data.token) {
        localStorage.setItem('token', response.data.token);
        setToken(response.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      }
    } catch (error) {
      // ignore
    }
  };

  // ------------------------------------------------------------------
  // 4. login
  // ------------------------------------------------------------------
  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/unified-login`, {
        email,
        password,
      });

      if (response.data.success) {
        const { user, company: companyData, token: authToken } = response.data.data;

        if (!authToken) {
          return { success: false, error: 'No authentication token received' };
        }

        localStorage.setItem('token', authToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;

        let userType = user?.userType;
        if (!userType) {
          if (companyData || user?.userCode?.startsWith('USERCOMP')) {
            userType = 'company';
          } else {
            userType = 'system';
          }
        }

        setCurrentUser(user);
        setUserType(userType);
        setCompany(companyData);
        setToken(authToken);

        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userType', userType);
        if (companyData) {
          localStorage.setItem('company', JSON.stringify(companyData));
        } else if (userType === 'company' && user.company) {
          localStorage.setItem('company', JSON.stringify({ _id: user.company }));
        }

        return {
          success: true,
          userType,
          user,
          company: companyData,
        };
      } else {
        return { success: false, error: response.data.message };
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed. Please try again.',
      };
    }
  };

  // ------------------------------------------------------------------
  // 5. logout
  // ------------------------------------------------------------------
  const logout = async () => {
    const userType = localStorage.getItem('userType');

    try {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          await axios.post(`${API_URL}/api/auth/unified-logout`, { userType });
        } catch (serverError) {
          // ignore server errors on logout
        }
      }

      clearAuth();
      navigate('/login', { replace: true });

      setTimeout(() => {
        window.location.reload();
      }, 100);
    } catch (error) {
      clearAuth();
      window.location.href = '/login';
    }
  };

  const simpleLogout = () => {
    clearAuth();
    navigate('/login', { replace: true });
    setTimeout(() => {
      window.location.reload();
    }, 50);
  };

  const clearAuth = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userType');
    localStorage.removeItem('company');

    sessionStorage.clear();

    setToken(null);
    setCurrentUser(null);
    setUserType(null);
    setCompany(null);

    delete axios.defaults.headers.common['Authorization'];
  };

  // ------------------------------------------------------------------
  // 6. isAuthenticated
  // ------------------------------------------------------------------
  const isAuthenticated = () => {
    const hasToken = !!localStorage.getItem('token');
    const hasUser = !!localStorage.getItem('user');
    const hasUserType = !!localStorage.getItem('userType');

    const userType = localStorage.getItem('userType');
    if (userType === 'company') {
      return hasToken && hasUser && hasUserType;
    }

    return !!currentUser && hasToken;
  };

  // ------------------------------------------------------------------
  // 7. getAuthStatus
  // ------------------------------------------------------------------
  const getAuthStatus = () => ({
    hasToken: !!localStorage.getItem('token'),
    hasUser: !!localStorage.getItem('user'),
    hasUserType: !!localStorage.getItem('userType'),
    hasCompany: !!localStorage.getItem('company'),
    contextUser: !!currentUser,
    contextUserType: userType,
    contextCompany: !!company,
    loading,
  });

  // ------------------------------------------------------------------
  // 8. Permission helpers
  // ------------------------------------------------------------------
  const hasRole = (roles) => {
    if (!currentUser) return false;
    if (Array.isArray(roles)) return roles.includes(currentUser.role);
    return currentUser.role === roles;
  };

  const hasPermission = (permission) => {
    if (!currentUser) return false;

    const permissions = {
      system: {
        developer: ['all', 'manage_system_users', 'manage_companies', 'view_system_reports', 'manage_settings'],
        admin: ['manage_system_users', 'manage_companies', 'view_system_reports', 'manage_settings'],
      },
      company: {
        admin: ['all', 'manage_company_users', 'manage_inventory', 'view_finance', 'manage_settings', 'manage_company'],
        sales: ['manage_sales', 'view_products', 'create_invoices', 'view_dashboard'],
        cashier: ['manage_payments', 'view_sales', 'process_transactions', 'view_dashboard'],
        purchase: ['manage_purchases', 'view_suppliers', 'create_orders', 'view_dashboard'],
        user: ['basic_access', 'view_dashboard'],
      },
    };

    const userPermissions = permissions[userType]?.[currentUser.role] || [];
    return userPermissions.includes('all') || userPermissions.includes(permission);
  };

  const canAccess = (requiredPermissions) => {
    if (!currentUser) return false;
    if (!Array.isArray(requiredPermissions)) {
      requiredPermissions = [requiredPermissions];
    }
    return requiredPermissions.some((p) => hasPermission(p));
  };

  const getUserDisplayName = () => {
    if (!currentUser) return '';
    return currentUser.name || currentUser.Usercomp_sure_name || currentUser.email || '';
  };

  const getUserAvatarInitials = () => {
    if (!currentUser) return 'U';
    const name = currentUser.name || currentUser.Usercomp_sure_name || currentUser.email || '';
    return name.charAt(0).toUpperCase();
  };

  const getCompanyId = () => {
    if (userType === 'company' && company) {
      return company._id || company.id;
    }
    return null;
  };

  const canViewSettings = () => {
    if (!currentUser) return false;
    if (userType === 'system') {
      return currentUser.role === 'developer' || currentUser.role === 'admin';
    } else if (userType === 'company') {
      return currentUser.role === 'admin';
    }
    return false;
  };

  const canManageSystemUsers = () => {
    if (!currentUser) return false;
    return userType === 'system' && (currentUser.role === 'developer' || currentUser.role === 'admin');
  };

  const canManageCompanyUsers = () => {
    if (!currentUser) return false;
    if (userType === 'system') {
      return currentUser.role === 'developer' || currentUser.role === 'admin';
    } else if (userType === 'company') {
      return currentUser.role === 'admin';
    }
    return false;
  };

  const getUserPermissions = () => {
    if (!currentUser) return [];
    const permissions = {
      system: {
        developer: ['all', 'manage_system_users', 'manage_companies', 'view_system_reports', 'manage_settings'],
        admin: ['manage_system_users', 'manage_companies', 'view_system_reports', 'manage_settings'],
      },
      company: {
        admin: ['all', 'manage_company_users', 'manage_inventory', 'view_finance', 'manage_settings', 'manage_company'],
        sales: ['manage_sales', 'view_products', 'create_invoices', 'view_dashboard'],
        cashier: ['manage_payments', 'view_sales', 'process_transactions', 'view_dashboard'],
        purchase: ['manage_purchases', 'view_suppliers', 'create_orders', 'view_dashboard'],
        user: ['basic_access', 'view_dashboard'],
      },
    };
    return permissions[userType]?.[currentUser.role] || [];
  };

  // ------------------------------------------------------------------
  // 9. updateProfile  (uses /unified-me)
  // ------------------------------------------------------------------
  const updateProfile = async (userData) => {
    try {
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        return {
          success: false,
          error: "You're not logged in. Please log in again.",
        };
      }

      const payload = {
        name: userData.name,
        email: userData.email,
      };

      if (userData.password && userData.password.trim() !== "") {
        payload.password = userData.password;
      }

      const response = await axios.put(
        `${API_URL}/api/auth/unified-me`,
        payload,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.success) {
        const updated = response.data.data || {};

        const newUserData = {
          ...currentUser,
          ...updated,
          name:
            updated.name ||
            updated.Usercomp_sure_name ||
            userData.name ||
            currentUser.name,
          email:
            updated.email ||
            updated.Usercomp_email ||
            userData.email ||
            currentUser.email,
        };

        setCurrentUser(newUserData);
        localStorage.setItem('user', JSON.stringify(newUserData));

        return { success: true, data: updated };
      }

      return { success: false, error: response.data.message };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message || 'Failed to update profile',
      };
    }
  };

  // ------------------------------------------------------------------
  // 10. registerSystemUser / registerCompany
  // ------------------------------------------------------------------
  const registerSystemUser = async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, userData);
      if (response.data.success) {
        if (userData.autoLogin !== false) {
          const loginResult = await login(userData.email, userData.password);
          if (loginResult.success) {
            return { success: true, data: response.data.data };
          }
        }
        return { success: true, data: response.data.data };
      }
      return { success: false, error: response.data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Registration failed',
      };
    }
  };

  const registerCompany = async (companyData) => {
    try {
      const response = await axios.post(`${API_URL}/api/company`, companyData);
      if (response.data.success) {
        const { admin_user } = response.data.data;
        const loginResult = await login(admin_user.Usercomp_email, companyData.password);
        if (loginResult.success) {
          return {
            success: true,
            data: response.data.data,
            autoLoggedIn: true,
          };
        }
        return { success: true, data: response.data.data };
      }
      return { success: false, error: response.data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Company registration failed',
      };
    }
  };

  // ------------------------------------------------------------------
  // 11. Value provider
  // ------------------------------------------------------------------
  const value = {
    currentUser,
    userType,
    company,
    loading,
    token,
    login,
    logout,
    simpleLogout,
    registerSystemUser,
    registerCompany,
    updateProfile,
    clearAuth,
    refreshCompanyToken,
    hasRole,
    hasPermission,
    canAccess,
    getUserDisplayName,
    getUserAvatarInitials,
    getCompanyId,
    getUserPermissions,
    getAuthStatus,
    isAuthenticated: isAuthenticated(),

    canViewSettings,
    canManageSystemUsers,
    canManageCompanyUsers,

    isSystemAdmin: userType === 'system' && currentUser?.role === 'admin',
    isDeveloper: userType === 'system' && currentUser?.role === 'developer',
    isCompanyAdmin: userType === 'company' && currentUser?.role === 'admin',
    isCompanyUser: userType === 'company',
    isSystemUser: userType === 'system',

    user: currentUser,
    register: registerSystemUser,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};

export default AuthContext;